import React, { useState, useContext, useMemo } from 'react';
import {
    Box, Typography, Paper, Badge, List, ListItem, ListItemButton,
    ListItemIcon, ListItemText, Divider, Fade, Chip, Stack, useTheme
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import useDataFetch from '../../utils/useDataFetch';
import { ActiveTeamContext } from '../../context/ActiveTeamContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';

const ServerDay = (props) => {
    const { appointments = [], day, outsideCurrentMonth, ...other } = props;
    const theme = useTheme();

    const dayAppointments = appointments.filter(app => {
        const appDate = dayjs(app.dat);
        return appDate.isSame(day, 'day');
    });

    const isSelected = !outsideCurrentMonth && dayAppointments.length > 0;
    const hasGame = dayAppointments.some(a => a.typ === 'Spiel');

    return (
        <Badge
            key={day.toString()}
            overlap="circular"
            badgeContent={isSelected ? (dayAppointments.length > 1 ? dayAppointments.length : '') : undefined}
            sx={{
                '& .MuiBadge-badge': {
                    right: '50%',
                    bottom: 4,
                    top: 'auto',
                    transform: 'translate(50%, 0)',
                    width: 6,
                    height: 6,
                    minWidth: 0,
                    bgcolor: hasGame ? theme.palette.error.main : theme.palette.primary.main,
                    boxShadow: `0 0 10px ${hasGame ? theme.palette.error.main : theme.palette.primary.main}`,
                    ...(dayAppointments.length > 1 && {
                        width: 14,
                        height: 14,
                        fontSize: '0.6rem',
                        fontWeight: 900,
                        color: '#000',
                        bottom: 2
                    })
                }
            }}
        >
            <PickersDay
                {...other}
                outsideCurrentMonth={outsideCurrentMonth}
                day={day}
                sx={{
                    fontWeight: 700,
                    '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: '#000',
                        '&:hover': { bgcolor: 'primary.dark' }
                    }
                }}
            />
        </Badge>
    );
};

const CalendarView = () => {
    const theme = useTheme();
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const { activeTeamId } = useContext(ActiveTeamContext);
    const { user } = useAuth();
    const navigate = useNavigate();

    const { data, isLoading } = useDataFetch({
        url: activeTeamId && user?.id ? `/termine/allAppointments?id=${user.id}&teamId=${activeTeamId}` : null
    });

    const appointments = useMemo(() => data?.appointments || [], [data]);

    const selectedAppointments = useMemo(() => {
        return appointments.filter(app => dayjs(app.dat).isSame(selectedDate, 'day'));
    }, [appointments, selectedDate]);

    const handleDateChange = (newDate) => {
        setSelectedDate(newDate);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 4 }}>

                {/* Left Side: Calendar */}
                <Box sx={{ flex: 1.2, display: 'flex', justifyContent: 'center' }}>
                    <DateCalendar
                        value={selectedDate}
                        onChange={handleDateChange}
                        loading={isLoading}
                        slots={{ day: ServerDay }}
                        slotProps={{ day: { appointments: appointments } }}
                        sx={{
                            width: '100%',
                            maxHeight: 'none',
                            height: 'auto',
                            '& .MuiPickersCalendarHeader-root': { pl: 2, pr: 2, mt: 1, mb: 1 },
                            '& .MuiPickersCalendarHeader-label': { fontWeight: 900, fontSize: '1.1rem' },
                            '& .MuiDayCalendar-header': { mb: 1 },
                            '& .MuiDayCalendar-weekDayLabel': { fontWeight: 800, color: 'text.secondary', fontSize: '0.75rem' },
                            '& .MuiPickersDay-today': { border: '2px solid', borderColor: 'primary.main' }
                        }}
                    />
                </Box>

                <Divider />

                {/* Right Side: Appointment Details */}
                <Box sx={{ flex: 1, minWidth: { xs: '100%', lg: '300px' } }}>
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                        <Box sx={{ p: 1, borderRadius: "12px", bgcolor: 'primary.main', color: '#000' }}>
                            <EventRoundedIcon />
                        </Box>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                                {selectedDate.format('DD. MMMM')}
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
                                {selectedAppointments.length} Termine geplant
                            </Typography>
                        </Box>
                    </Stack>

                    {selectedAppointments.length === 0 ? (
                        <Fade in timeout={500}>
                            <Box sx={{ textAlign: 'center', py: 8, px: 2, borderRadius: "12px", border: '1px dashed', borderColor: 'divider' }}>
                                <InfoRoundedIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 2 }} />
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    Keine Termine an diesem Tag.
                                </Typography>
                            </Box>
                        </Fade>
                    ) : (
                        <List sx={{ width: '100%', p: 0 }}>
                            {selectedAppointments.map((app, index) => (
                                <Fade in timeout={300 + (index * 150)} key={app.id}>
                                    <Paper
                                        sx={{
                                            mb: 2,
                                            borderRadius: "12px",
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            overflow: 'hidden',
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                                            '&:hover': {
                                                borderColor: 'primary.main',
                                                transform: 'translateX(8px)',
                                                bgcolor: 'rgba(190, 242, 100, 0.05)'
                                            }
                                        }}
                                    >
                                        <ListItemButton
                                            onClick={() => navigate(`/termine/${app.id}`)}
                                            sx={{ p: 2.5, flexDirection: 'column', alignItems: 'flex-start' }}
                                        >
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 1.5, alignItems: 'flex-start' }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2, color: 'text.primary', maxWidth: '70%' }}>
                                                    {app.titel}
                                                </Typography>
                                                <Chip
                                                    label={app.typ}
                                                    size="small"
                                                    sx={{
                                                        fontWeight: 800,
                                                        fontSize: '0.65rem',
                                                        bgcolor: app.typ === 'Spiel' ? 'error.main' : app.typ === 'Training' ? 'primary.main' : 'divider',
                                                        color: (app.typ === 'Spiel' || app.typ === 'Training') ? '#000' : 'text.primary'
                                                    }}
                                                />
                                            </Box>

                                            <Stack spacing={1}>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <AccessTimeRoundedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                                    <Typography variant="body2" sx={{ fontFamily: '"Fira Code", monospace', fontWeight: 600, fontSize: '0.8rem' }}>
                                                        {app.start_time.substring(0, 5)} {app.end_time ? `- ${app.end_time.substring(0, 5)}` : ''}
                                                    </Typography>
                                                </Stack>

                                                {app.ort && (
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        <LocationOnRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                                            {app.ort}
                                                        </Typography>
                                                    </Stack>
                                                )}
                                            </Stack>
                                        </ListItemButton>
                                    </Paper>
                                </Fade>
                            ))}
                        </List>
                    )}
                </Box>
            </Box>
        </LocalizationProvider>
    );
};

export default CalendarView;
